/**
 * SelectionIndicator - Triangle wedge indicator for photo selection
 *
 * Displays a small triangular wedge in the corner of a photo to indicate selection.
 * Designed to be positioned absolutely within a relative parent container.
 *
 * @module SelectionIndicator
 */

import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const selectionIndicatorVariants = cva(
  'absolute w-0 h-0 pointer-events-none',
  {
    variants: {
      size: {
        sm: '',
        md: '',
        lg: '',
      },
      position: {
        'top-right': 'top-0 right-0',
        'top-left': 'top-0 left-0',
        'bottom-right': 'bottom-0 right-0',
        'bottom-left': 'bottom-0 left-0',
      },
    },
    defaultVariants: {
      size: 'md',
      position: 'top-right',
    },
  }
);

// Size-specific border widths for the triangle
const sizeStyles = {
  sm: { borderWidth: '0 16px 16px 0' },
  md: { borderWidth: '0 22px 22px 0' },
  lg: { borderWidth: '0 32px 32px 0' },
} as const;

export interface SelectionIndicatorProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof selectionIndicatorVariants> {
  /**
   * Whether the indicator is visible
   * @default true
   */
  visible?: boolean;
}

/**
 * SelectionIndicator - Triangle wedge for photo selection state
 *
 * Creates a corner triangle using CSS borders technique.
 * Position is always in the corner specified by the position prop.
 */
export function SelectionIndicator({
  size = 'md',
  position = 'top-right',
  visible = true,
  className,
  ...props
}: SelectionIndicatorProps) {
  if (!visible) return null;

  const sizeKey = size ?? 'md';
  const borderStyle = sizeStyles[sizeKey];

  return (
    <div
      className={cn(selectionIndicatorVariants({ size, position }), className)}
      style={{
        borderStyle: 'solid',
        ...borderStyle,
        // Triangle color: primary on the right edge, transparent elsewhere
        borderColor: 'transparent rgb(var(--color-primary)) transparent transparent',
      }}
      aria-hidden="true"
      data-testid="selection-indicator"
      {...props}
    />
  );
}
